/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import { DateTime } from "luxon";
import DateUtil from "../Utils/DateUtil";
import formatDateTimeRange from "./formatDateTimeRange";
import { cultures, languages } from "./languages";
import ValueType from "./ValueType";
import { ermittleWaehrung } from "./waehrung";

export const defaultDateOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
};

export default function format(value: any, valueType?: string, locale?: string): string {
    if (!locale || (!(Object.values(cultures).includes(locale)) && !(Object.values(languages).includes(locale)))) {
        locale = cultures.enGB;
    }

    const formatValue = dispatchFormatValue(valueType as ValueType);
    return formatValue(value, locale);
}

function dispatchFormatValue(valueType: ValueType) {
    switch (valueType) {
        case "DateTimeRange":
            return formatDateTimeRange;
        case "DateTime":
            return formatDateTime;
        case "Date":
            return formatDate;
        case "DateNoWeekday":
            return formatDateNoWeekday;
        case "Time":
            return formatTime;
        case "Prozent":
            return formatProzent;
        case "Menge":
            return formatMenge;
        case "Preis":
            return formatPreis;
        case "PreisSigned":
            return formatPreisSigned;
        case "Weekday":
            return formatWeekday;
        case "ZeitraumChip":
            return formatZeitraumChip;
        case "TimeForBuchungsportal":
            return formatTimeForBuchungsportal;
        case "DateForBuchungsportal":
            return formatDateForBuchungsportal;
        case "DateForBuchungsportalWithoutWeekday":
            return (date: Date, language: string) => formatDateForBuchungsportal(date, language, true);
        case "DateTimeForBuchungsportal":
            return formatDateTimeForBuchungsportal;
        default:
            return formatUnbekannt;
    }
}

function formatDateTimeForBuchungsportal(date: Date, language: string) {
    return formatDateForBuchungsportal(date, language) + " " + formatTimeForBuchungsportal(date, language);
}

function formatDateForBuchungsportal(date: Date, language: string, withoutWeekday: boolean = false) {
    return new Intl.DateTimeFormat(language, {
        weekday: withoutWeekday ? undefined : "short",
        year: "numeric",
        month: "short",
        day: "2-digit",
    }).format(date);
}

function formatTimeForBuchungsportal(date: Date, language: string) {
    return new Intl.DateTimeFormat(language, {
        hour: "2-digit",
        minute: "2-digit",
    }).format(date);
}

function formatZeitraumChip({ beginn, ende }: { beginn: string, ende: string }, locale: string) {
    const formatter = new Intl.DateTimeFormat(locale, {
        dateStyle: "medium",
    });

    const format = (d: Date) => formatter.format(d);

    if (DateUtil.isValidDate(beginn) && DateUtil.isValidDate(ende)) {
        return `${format(new Date(beginn))} - ${format(new Date(ende))}`;
    }

    if (DateUtil.isValidDate(beginn)) {
        return `Vom ${format(new Date(beginn))}`;
    }

    if (DateUtil.isValidDate(ende)) {
        return `Bis ${format(new Date(ende))}`;
    }

    return "";
}

function formatDateTime(
    value: string,
    locale: string,
    dateOptions: Intl.DateTimeFormatOptions = defaultDateOptions,
    showMinutes: boolean = true): string {
    if (!DateUtil.isValidDate(value)) {
        return value;
    }

    const date = formatDate(value, locale, dateOptions);
    const time = formatTime(value, locale, showMinutes);
    return `${date} ${time}`;
}

function formatDate(
    value: string,
    locale: string,
    dateOptions: Intl.DateTimeFormatOptions = defaultDateOptions): string {
    if (!DateUtil.isValidDate(value)) {
        return value;
    }

    const weekday = formatWeekday(value, locale);
    const date = new Intl.DateTimeFormat(locale, dateOptions).format(new Date(value));
    return `${weekday}, ${date}${!dateOptions.month ? "." : ""}`;
}

function formatDateNoWeekday(
    value: string,
    locale: string,
    dateOptions: Intl.DateTimeFormatOptions = defaultDateOptions): string {
    if (!DateUtil.isValidDate(value)) {
        return value;
    }

    const date = new Intl.DateTimeFormat(locale, dateOptions).format(new Date(value));
    return `${date}`;
}

function formatTime(value: string, locale: string, showMinutes: boolean = true): string {
    if (!DateUtil.isValidDate(value)) {
        const date = DateTime.now().toISO();
        const newValue = DateUtil.setTime(date, value);
        if (!DateUtil.isValidDate(newValue) || !DateTime.fromISO(value).isValid) {
            return value;
        }

        value = newValue;
    }

    return new Intl.DateTimeFormat(locale, {
        hour: "numeric",
        minute: showMinutes ? "numeric" : undefined,
        hour12: false,
    }).format(new Date(value));
}

function formatProzent(value: any, locale: string): string {
    if (value === null) {
        return "";
    }

    const prozent = Number(value) / 100;
    if (isNaN(prozent)) {
        return value;
    }

    return new Intl.NumberFormat(locale, {
        style: "percent",
        minimumIntegerDigits: 1,
        minimumFractionDigits: 0,
        maximumFractionDigits: 8,
    }).format(prozent);
}

function formatMenge(value: any, locale: string): string {
    if (value === null) {
        return "";
    }

    const menge = Number(value);
    if (isNaN(menge)) {
        return value;
    }

    return new Intl.NumberFormat(locale, {
        minimumIntegerDigits: 1,
        minimumFractionDigits: 0,
        maximumFractionDigits: 8,
    }).format(menge);
}

function formatPreis(value: any, locale: string): string {
    const preis = Number(value);
    if (isNaN(preis)) {
        return value;
    }

    return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: ermittleWaehrung(),
    }).format(preis);
}

function formatPreisSigned(value: any, locale: string): string {
    const preis = Number(value);
    if (isNaN(preis)) {
        return value;
    }

    return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: ermittleWaehrung(),
        signDisplay: "exceptZero",
    }).format(preis);
}

function formatUnbekannt(value: any, locale: string) {
    return value;
}

function formatWeekday(value: any, locale: string) {
    if (value.startsWith("1970-01-01")) {
        value = 0;
    }

    if (!DateUtil.isValidDate(value)) {
        return value;
    }

    return new Intl.DateTimeFormat(locale, { weekday: "short" }).format(new Date(value));
}
